import { computed, type Ref } from 'vue'
import type { TimeOption } from '@/composables/useDetailsFormBase'
import { storeToRefs } from 'pinia'
import { useTimeStore } from '@/stores/time'
import { isToday } from 'date-fns'
import { useFormatDate } from '@/composables/useFormatDate'

function useTimeOptions(date: Ref<Date>, interval: number = 15) {
  const { currentTime } = storeToRefs(useTimeStore())
  const { formatTime } = useFormatDate()

  if (interval <= 0) {
    throw new Error('interval must be greater than 0')
  }

  const timeOptions = computed<TimeOption[]>(() => {
    const isTodayDate = isToday(date.value)

    const start = isTodayDate ? getNextIntervalValue(currentTime.value) : 0

    return calculateTimeOptions(start)
  })

  function getNextIntervalValue(date: Date): number {
    const totalMinutes = date.getHours() * 60 + date.getMinutes()
    const rounded = Math.ceil(totalMinutes / interval) * interval
    return rounded === totalMinutes ? rounded + interval : rounded
  }

  function calculateTimeOptions(startMinutes: number): TimeOption[] {
    const result: TimeOption[] = []

    if (startMinutes >= 24 * 60) return []

    for (let total = startMinutes; total < 24 * 60; total += interval) {
      const h = Math.floor(total / 60)
      const m = total % 60

      const tempDate = new Date()
      tempDate.setHours(h, m, 0, 0)

      result.push({
        title: formatTime(tempDate),
        value: total
      })
    }

    return result
  }

  return {
    timeOptions
  }
}

export default useTimeOptions
